import React from 'react';
import TooltipCustom from 'ui/atoms/Tooltip/Tooltip';
import type { EvaluationScale, ScoreValue } from './useScore';
import useScore from './useScore';

/**
 * Score - Renders a student grade with a colour that reflects its pedagogical status.
 *
 * The status is also exposed as visually hidden text, so colour is never the
 * only carrier of meaning (WCAG 1.4.1).
 */

type ScoreProps = {
  evaluationScale?: EvaluationScale | null;
  score?: ScoreValue;
  scoreScale?: string;
  toolTipText?: string;
};

function Score({ evaluationScale = null, score, scoreScale, toolTipText }: Readonly<ScoreProps>): JSX.Element {
  const { displayValue, shouldShowTooltipMark, status, statusAnnouncement } = useScore({ evaluationScale, score, scoreScale, toolTipText });

  // The status travels as visually hidden text rather than as an aria-label:
  // a label on a bare <span> is ignored by most screen readers, and adding
  // role="img" to rescue it would make them announce a spurious "image".
  const scoreElement = (
    <span className={`score score--${status}`}>
      {displayValue}
      {shouldShowTooltipMark && <span aria-hidden="true"> *</span>}
      <span className="sr-only">{statusAnnouncement}</span>
    </span>
  );

  if (!toolTipText) return scoreElement;

  return (
    <TooltipCustom placement="top" title={toolTipText}>
      {scoreElement}
    </TooltipCustom>
  );
}

export default Score;
